/**
 * What the web call room's two ring UIs show (IncomingCallOverlay, a ring
 * when this person is not in a call; CallWaitingOverlay, a second call in a
 * call). Pure logic, so the rules can be tested without a DOM.
 *
 * One order on both (web-vrs.html, approved 2026-10-29): the caller's name
 * first, then "Video call" and the seconds left; in a call, Hold and answer ·
 * Decline · VideoMail · End and answer; else Decline · VideoMail · Answer.
 */
/** How long a ring lasts when its message gives no deadline (the server's still-active reminder). */
export const RING_WINDOW_MS = 30000;
/**
 * When the ring stops (epoch ms): its ringDeadline, else its expiresAt, else
 * the ring window from when this device received it.
 *
 * @param {IRingFacts} ring - The ring message.
 * @param {number} receivedAt - When this device received it.
 * @returns {number}
 */
export function ringEndsAt(ring, receivedAt) {
    if (typeof ring.ringDeadline === 'number' && Number.isFinite(ring.ringDeadline)) {
        return ring.ringDeadline;
    }
    const expires = ring.expiresAt ? Date.parse(ring.expiresAt) : NaN;
    return Number.isFinite(expires) ? expires : receivedAt + RING_WINDOW_MS;
}
/**
 * Whole seconds left on a ring, never below 0 and never above the ring window.
 *
 * @param {number} endsAt - When the ring stops (ringEndsAt).
 * @param {number} now - Now.
 * @returns {number}
 */
export function ringSecondsLeft(endsAt, now) {
    return Math.max(0, Math.min(RING_WINDOW_MS / 1000, Math.ceil((endsAt - now) / 1000)));
}
/**
 * Whether this ring can go to VideoMail through the path the web portal's
 * "Send to VideoMail" uses (p2p_decline with sendToVoicemail: the server
 * gives the caller the VideoMail offer, vrs-server/ws/p2p-handlers.js). The
 * server sends a group call or an interpreter's call-back to a plain decline,
 * so those get no VideoMail; neither does a conference invitation, a hearing
 * caller on the VRS number (no direct call to decline), or a brand without
 * VideoMail.
 *
 * @param {IRingFacts} ring - The ring message.
 * @param {boolean} videoMailEnabled - The brand has VideoMail (features.voicemail).
 * @returns {boolean}
 */
export function canSendRingToVideoMail(ring, videoMailEnabled) {
    return videoMailEnabled
        && Boolean(ring.callerId)
        && !ring.callback
        && !ring.conferenceInvitation
        && !ring.inboundVrs
        && ring.groupCall !== true
        && Number(ring.participantCount || 0) <= 2;
}
/** Maple's mark on a dark splash: the white leaf, as on the phone apps (brandAssets.native.ts). */
export const MAPLE_RING_MARK = 'images/maple-icon-white.png';
/**
 * The brand's mark on the ring when the caller has no photo (Robert,
 * 2026-09-28; the phone apps' ring sheet does the same): Maple's white leaf,
 * else the tenant's white logo.
 *
 * @param {string} tenantId - The tenant.
 * @param {string} logoWhite - The tenant's white logo (getLogoWhiteUrl).
 * @returns {string}
 */
export function ringBrandMark(tenantId, logoWhite) {
    return /^maple(?:$|-)/.test(String(tenantId || '')) ? MAPLE_RING_MARK : logoWhite;
}
/**
 * Up to two initials for the caller's round mark (the in-call banner, as on
 * the phone apps' banner).
 *
 * @param {string} name - The caller's name.
 * @returns {string}
 */
export function callerInitials(name) {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);
    return words.slice(0, 2).map(word => Array.from(word)[0].toUpperCase()).join('');
}
